import { Children, forwardRef, isValidElement, useLayoutEffect } from 'react';
import type { HTMLAttributes, ReactElement } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { setRef } from '@mui/material/utils';
import { List, useListRef, type RowComponentProps } from 'react-window';

const ROW_HEIGHT_PX = 48;
const MAX_VISIBLE_ROWS = 8;
const OVERSCAN_COUNT = 5;

type RowProps = { itemData: ReactElement[] };

type ListboxProps = HTMLAttributes<HTMLElement> & { sx?: SxProps<Theme> };

function VirtualRow({ style, itemData, index }: RowComponentProps<RowProps>): ReactElement | null {
  const row = itemData[index];

  if (!row) {
    return null;
  }

  return (
    <div role="presentation" style={{ ...style, paddingLeft: 8, paddingRight: 8 }}>
      {row}
    </div>
  );
}

export const VirtualizedAutocompleteListbox = forwardRef<HTMLDivElement, ListboxProps>(
  function VirtualizedAutocompleteListbox(props, ref) {
    const { children, ...other } = props;
    const itemData = Children.toArray(children).filter(isValidElement);

    const listApiRef = useListRef(null);

    const count = itemData.length;
    const height = count === 0 ? 1 : Math.min(count, MAX_VISIBLE_ROWS) * ROW_HEIGHT_PX;

    useLayoutEffect(() => {
      setRef(ref, listApiRef.current?.element ?? null);
    }, [ref, count, listApiRef]);

    return (
      <List<RowProps>
        {...other}
        listRef={listApiRef}
        role="listbox"
        rowCount={count}
        rowHeight={ROW_HEIGHT_PX}
        rowProps={{ itemData }}
        rowComponent={VirtualRow}
        overscanCount={OVERSCAN_COUNT}
        style={{ height }}
      />
    );
  }
);
